import { useEffect, useRef } from "react";

/**
 * AstralCursor
 * ----------------------------------------------------------
 * Replaces the native pointer with a small astral map:
 *   · a central brass planet dot
 *   · two concentric orbit rings + two orbiting honey stars
 *   · on click, a warm scatter of varied sparkles bursts
 *     OUTSIDE the outer ring
 *
 * - Soft-follow (lerp) so the cursor glides, not snaps.
 * - Grows slightly when hovering interactive / data-cosmic elements.
 * - Falls back to native cursor on touch and reduced-motion.
 * - Built entirely via createElementNS() — NO innerHTML anywhere,
 *   so the SVG tree cannot be tampered with by any string input.
 */
const SVG_NS = "http://www.w3.org/2000/svg";

// Helper: create a namespaced SVG element with attributes.
function svg(tag, attrs = {}, parent = null) {
  const el = document.createElementNS(SVG_NS, tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (v === undefined || v === null) continue;
    el.setAttribute(k, String(v));
  }
  if (parent) parent.appendChild(el);
  return el;
}

// Scattered sparkles: unique shape, size, position, tilt, delay.
// All positioned OUTSIDE the outer ring (r=18 in a 64x64 viewBox centered
// at (32,32)). Distances 31–46 SVG units so every spark has at least a
// 13-px visual gap between the ring's edge and its inner edge.
const SPARKS = [
  { d: "M0 -7 L1.4 -2.4 L6 -3.5 L2.6 -1 L7 3 L1.7 1 L0 6 L-1.7 1 L-7 3 L-2.6 -1 L-6 -3.5 L-1.4 -2.4 Z",
    fill: "#F5D77E", x: 66.8, y: 41.5, tilt: 0,   delay: 0   },
  { d: "M0 -5 L1 -1 L5 0 L1 1 L0 5 L-1 1 L-5 0 L-1 -1 Z",
    fill: "#E0B84C", x: 44.2, y: 70.5, tilt: 30,  delay: 40  },
  { circle: 2.2,
    fill: "#F5D77E", x: -3.2, y: 61.1, tilt: 0,   delay: 110 },
  { d: "M0 -6 L0.5 -0.5 L5 0 L0.5 0.5 L0 6 L-0.5 0.5 L-5 0 L-0.5 -0.5 Z",
    fill: "#F2C778", x: 3.5,  y: 18.5, tilt: -22, delay: 170 },
  { d: "M0 -5.5 L1.7 -1.7 L5.5 -1 L2.4 1 L3.5 4.7 L0 2.4 L-3.5 4.7 L-2.4 1 L-5.5 -1 L-1.7 -1.7 Z",
    fill: "#E0B84C", x: 37.6, y: -7.4, tilt: 15,  delay: 230 },
  { d: "M-0.7 -3 L0.7 -3 L0.7 -0.7 L3 -0.7 L3 0.7 L0.7 0.7 L0.7 3 L-0.7 3 L-0.7 0.7 L-3 0.7 L-3 -0.7 L-0.7 -0.7 Z",
    fill: "#F5D77E", x: 65.8, y: 19.7, tilt: -30, delay: 300 },
];

const HOT_SELECTOR =
  '[data-cosmic="true"], a, button, [role="button"], [role="link"], summary, label, select, .cursor-pointer';

const CURSOR_STYLES = `
html, body { cursor: none; }
input, textarea, [contenteditable="true"] { cursor: text; }
.astral-cursor {
  position: fixed; top: 0; left: 0;
  width: 64px; height: 64px;
  margin-left: -32px; margin-top: -32px;
  pointer-events: none; z-index: 9999;
  transform: translate3d(-200px, -200px, 0);
  will-change: transform;
  transition: opacity 220ms ease-out;
  opacity: 0;
}
.astral-cursor.is-ready { opacity: 1; }
.astral-cursor.is-hidden { opacity: 0; }
.astral-svg { display: block; overflow: visible; transition: transform 220ms cubic-bezier(0.22,1,0.36,1); }
.astral-cursor.is-hot .astral-svg { transform: scale(1.18); filter: drop-shadow(0 0 6px rgba(224,184,76,0.55)); }
@keyframes astral-spin-a { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
@keyframes astral-spin-b { from { transform: rotate(360deg); } to { transform: rotate(0deg); } }
.astral-orbit { transform-origin: 32px 32px; }
.astral-orbit--outer { animation: astral-spin-a 3.6s linear infinite; }
.astral-orbit--inner { animation: astral-spin-b 2.4s linear infinite; }
.astral-spark-inner {
  transform-origin: 0 0;
  transform: rotate(var(--tilt, 0deg)) scale(0.2);
  opacity: 0;
  pointer-events: none;
}
@keyframes astral-sparkle {
  0%   { opacity: 0;    transform: rotate(var(--tilt, 0deg))                scale(0.2); filter: drop-shadow(0 0 0 rgba(245,215,126,0)); }
  30%  { opacity: 1;    transform: rotate(calc(var(--tilt, 0deg) + 12deg)) scale(1.15); filter: drop-shadow(0 0 6px rgba(245,215,126,0.95)); }
  60%  { opacity: 0.75; transform: rotate(calc(var(--tilt, 0deg) + 20deg)) scale(0.95); }
  100% { opacity: 0;    transform: rotate(calc(var(--tilt, 0deg) + 34deg)) scale(0.5); filter: drop-shadow(0 0 0 rgba(245,215,126,0)); }
}
.astral-cursor.is-sparking .astral-spark-inner { animation: astral-sparkle 820ms ease-out both; }
@media (prefers-reduced-motion: reduce) { .astral-orbit, .astral-spark-inner { animation: none !important; } }
@media (hover: none) { html, body { cursor: auto; } .astral-cursor { display: none; } }
`;

// Build the entire astral SVG via createElementNS — no innerHTML, no strings-to-DOM.
function buildAstralSvg() {
  const root = svg("svg", {
    width: 64, height: 64, viewBox: "0 0 64 64",
    class: "astral-svg", overflow: "visible",
  });

  // gradients
  const defs = svg("defs", {}, root);
  [
    { id: "acStarA", stops: [["0%", "#F5D77E", 1], ["70%", "#E0B84C", 0.9], ["100%", "#E0B84C", 0]] },
    { id: "acStarB", stops: [["0%", "#F2C778", 1], ["70%", "#B08830", 0.9], ["100%", "#B08830", 0]] },
  ].forEach((g) => {
    const rg = svg("radialGradient", { id: g.id, cx: "50%", cy: "50%", r: "50%" }, defs);
    g.stops.forEach(([offset, color, opacity]) => {
      svg("stop", { offset, "stop-color": color, "stop-opacity": opacity }, rg);
    });
  });

  // outer ring
  const ringGroup = svg("g", { stroke: "#B08830", fill: "none", "stroke-width": "0.9", opacity: "0.85" }, root);
  svg("circle", { cx: 32, cy: 32, r: 18 }, ringGroup);

  // ring ticks
  const ticksGroup = svg("g", { stroke: "#B08830", "stroke-width": "0.7", opacity: "0.5" }, root);
  for (let i = 0; i < 12; i++) {
    const a = (i * Math.PI) / 6;
    svg("line", {
      x1: (32 + Math.cos(a) * 18).toFixed(2),
      y1: (32 + Math.sin(a) * 18).toFixed(2),
      x2: (32 + Math.cos(a) * 20).toFixed(2),
      y2: (32 + Math.sin(a) * 20).toFixed(2),
    }, ticksGroup);
  }

  // inner dashed ring
  svg("circle", { cx: 32, cy: 32, r: 10, fill: "none", stroke: "#B8622A", "stroke-width": "0.8", "stroke-dasharray": "2 3", opacity: "0.7" }, root);

  // rotating orbit stars
  const outerOrbit = svg("g", { class: "astral-orbit astral-orbit--outer" }, root);
  svg("circle", { cx: 50, cy: 32, r: 2.4, fill: "url(#acStarA)" }, outerOrbit);
  const innerOrbit = svg("g", { class: "astral-orbit astral-orbit--inner" }, root);
  svg("circle", { cx: 42, cy: 32, r: 1.7, fill: "url(#acStarB)" }, innerOrbit);

  // click-burst scattered sparkles
  const sparks = svg("g", { class: "astral-sparks" }, root);
  SPARKS.forEach((s, i) => {
    const outerG = svg("g", {
      class: `astral-spark astral-spark--${i}`,
      transform: `translate(${s.x} ${s.y})`,
    }, sparks);
    const innerG = svg("g", { class: "astral-spark-inner" }, outerG);
    // per-spark tilt + stagger via style — set via style property, not string parse
    innerG.style.setProperty("--tilt", `${s.tilt}deg`);
    innerG.style.animationDelay = `${s.delay}ms`;
    if (s.circle) {
      svg("circle", { cx: 0, cy: 0, r: s.circle, fill: s.fill }, innerG);
      svg("circle", { cx: 0, cy: 0, r: s.circle + 1.6, fill: s.fill, opacity: "0.32" }, innerG);
    } else {
      svg("path", { d: s.d, fill: s.fill }, innerG);
    }
  });

  // central planet
  svg("circle", { cx: 32, cy: 32, r: 2.1, fill: "#B8622A" }, root);
  svg("circle", { cx: 32, cy: 32, r: 4.5, fill: "none", stroke: "#B8622A", "stroke-width": "0.5", opacity: "0.35" }, root);

  return root;
}

export default function AstralCursor() {
  const rootRef = useRef(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.matchMedia("(hover: none)").matches) return;

    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    // Root cursor <div>
    const root = document.createElement("div");
    root.setAttribute("aria-hidden", "true");
    root.className = "astral-cursor";
    root.appendChild(buildAstralSvg());
    document.body.appendChild(root);

    // Style block (safe: textContent, not innerHTML)
    const style = document.createElement("style");
    style.setAttribute("data-astral-cursor", "true");
    style.textContent = CURSOR_STYLES;
    document.head.appendChild(style);
    rootRef.current = root;

    // Pointer soft-follow (lerp)
    let targetX = -200;
    let targetY = -200;
    let curX = -200;
    let curY = -200;
    let ready = false;
    let raf = 0;

    const onMove = (e) => {
      targetX = e.clientX;
      targetY = e.clientY;
      if (!ready) {
        ready = true;
        curX = targetX;
        curY = targetY;
        root.classList.add("is-ready");
      }
      const hot = e.target && e.target.closest && e.target.closest(HOT_SELECTOR);
      root.classList.toggle("is-hot", !!hot);
    };
    const onLeave = () => root.classList.add("is-hidden");
    const onEnter = () => root.classList.remove("is-hidden");
    const onDown = () => {
      // Reshuffle every spark's position + tilt slightly so no two bursts look
      // identical — small orbital jitter (±5px, ±20°, ±0.15 scale variance).
      const sparks = root.querySelectorAll(".astral-spark");
      sparks.forEach((outerG, i) => {
        const base = SPARKS[i];
        if (!base) return;
        const jx = (Math.random() - 0.5) * 10;
        const jy = (Math.random() - 0.5) * 10;
        const jt = (Math.random() - 0.5) * 40;
        outerG.setAttribute("transform", `translate(${(base.x + jx).toFixed(2)} ${(base.y + jy).toFixed(2)})`);
        const inner = outerG.querySelector(".astral-spark-inner");
        if (inner) {
          inner.style.setProperty("--tilt", `${(base.tilt + jt).toFixed(2)}deg`);
        }
      });
      root.classList.remove("is-sparking");
      // eslint-disable-next-line no-unused-expressions
      root.offsetWidth;
      root.classList.add("is-sparking");
    };
    const onUp = () => {};
    const onSparkEnd = (e) => {
      // Clear the class once the last spark's animation ends so it doesn't
      // linger between clicks (also cleaner for state/tests).
      if (e.target && e.target.classList && e.target.classList.contains("astral-spark-inner")) {
        root.classList.remove("is-sparking");
      }
    };
    root.addEventListener("animationend", onSparkEnd);

    const tick = () => {
      const easing = reduced ? 1 : 0.28;
      curX += (targetX - curX) * easing;
      curY += (targetY - curY) * easing;
      root.style.transform = `translate3d(${curX}px, ${curY}px, 0)`;
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    window.addEventListener("pointermove", onMove, { passive: true });
    window.addEventListener("pointerdown", onDown);
    window.addEventListener("pointerup", onUp);
    document.addEventListener("mouseleave", onLeave);
    document.addEventListener("mouseenter", onEnter);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerdown", onDown);
      window.removeEventListener("pointerup", onUp);
      document.removeEventListener("mouseleave", onLeave);
      document.removeEventListener("mouseenter", onEnter);
      root.removeEventListener("animationend", onSparkEnd);
      if (root.parentNode) root.parentNode.removeChild(root);
      if (style.parentNode) style.parentNode.removeChild(style);
    };
  }, []);

  return null;
}
